/**
 *  Application state reducer for managing global state transitions.
 * @module state
 */

import { AppAction, AppState } from "../types";

/**
 * Reducer function that handles all application state transitions.
 *
 * appReducer
 * @param {AppState} state - Current application state
 * @param {AppAction} action - Action to process
 * @returns {AppState} New application state
 * @module state
 * @category State Management
 *
 * @remarks
 * This reducer follows the Redux pattern and handles all state mutations
 * for the LOA Frontend application. It processes the following action types:
 *
 * | Action | Effect |
 * |--------|--------|
 * | `AUTH_LOGIN` | Sets user, token, and `isAuthenticated: true` |
 * | `AUTH_LOGOUT` | Clears user/token and sets `isAuthenticated: false` |
 * | `AUTH_ERROR` | Clears auth state and sets error message |
 * | `AUTH_LOADING` | Sets `isLoading: true` |
 * | `UI_TOGGLE_SIDEBAR` | Inverts `isSidebarOpen` |
 * | `UI_SET_ACTIVE_PANEL` | Updates `activePanelId` |
 * | `UI_SET_ACTIVE_ENTITY` | Updates `activeEntityId` (ID only, never a full object) |
 * | `UI_SET_ACTIVE_NAVITEM` | Updates `activeNavitemId` |
 * | `APP_START` | Initializes app name, env, and version |
 *
 * Filter state (`filterTerm`, case status filters) is intentionally absent.
 * Those values are local state owned by `ProtectedLayout`, not this reducer.
 *
 * The reducer is pure and always returns a new state object without
 * mutating the existing state.
 *
 * @example
 * ```typescript
 * import { useReducer } from 'react';
 * import { appReducer } from '../state/appReducer';
 *
 * const [state, dispatch] = useReducer(appReducer, initialState);
 *
 * // Dispatch a login action
 * dispatch({
 *   type: 'AUTH_LOGIN',
 *   payload: { user: userData, token: 'jwt-token' }
 * });
 * ```
 *
 * @see {@link AppAction} for available action types
 * @see {@link AppState} for the state shape
 * @see {@link AppProvider} for how this reducer is used
 */
export function appReducer(state: AppState, action: AppAction): AppState {
  const { type } = action;

  switch (type) {
    case "AUTH_LOGIN":
      return {
        ...state,
        auth: {
          user: action.payload.user,
          token: action.payload.token,
          isAuthenticated: true,
          isLoading: false,
          message: "",
        },
      };

    case "AUTH_LOGOUT":
      return {
        ...state,
        auth: {
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          message: "",
        },
      };

    case "AUTH_LOADING":
      return {
        ...state,
        auth: {
          ...state.auth,
          isLoading: true,
        },
      };

    case "AUTH_ERROR":
      return {
        ...state,
        auth: {
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          message: action.payload.error || "Authentication failed",
        },
      };

    case "UI_TOGGLE_SIDEBAR":
      return {
        ...state,
        ui: {
          ...state.ui,
          isSidebarOpen: !state.ui.isSidebarOpen,
        },
      };

    case "UI_SET_ACTIVE_PANEL":
      return {
        ...state,
        ui: {
          ...state.ui,
          activePanelId: action.payload.panelId,
        },
      };

    case "UI_SET_ACTIVE_ENTITY":
      return {
        ...state,
        ui: {
          ...state.ui,
          activeEntityId: action.payload.entityId,
        },
      };

    case "UI_SET_ACTIVE_NAVITEM":
      return {
        ...state,
        ui: {
          ...state.ui,
          activeNavitemId: action.payload.navitemId,
        },
      };

    case "APP_START":
      return {
        ...state,
        name: action.payload.name,
        env: action.payload.env,
        version: action.payload.version,
      };

    default:
      return state;
  }
}
